<template>
  <NuxtErrorBoundary @error="handleError">
    <slot />
    <template v-if="isPreviewEnabled" #error="{ error }">
      <template v-if="!isFatal(error)">
        <HeadlineBlock :headline="headline" :cols="12" />
        <Card>
          <pre>
Error:
{{ error }}
        </pre>
          <pre v-if="error.cause">
Cause:
{{ error }}
        </pre>
          <pre>
Child Props:
{{ childProps }}
        </pre>
        </Card>
      </template>
    </template>
  </NuxtErrorBoundary>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue';

// Card and HeadlineBlock are only rendered in Contentful preview mode when a
// child component throws (see template `v-if="isPreviewEnabled"` + `#error`).
// Importing them statically pulls the entire (non-tree-shakeable)
// `@hypercodestudio/basler-components` barrel — including three.js and the
// video.js stack — into the eager entry chunk on every page. Loading them
// lazily keeps that ~1.8 MB out of the entry bundle.
const Card = defineAsyncComponent(() =>
  import('@hypercodestudio/basler-components').then((m) => m.Card)
);
const HeadlineBlock = defineAsyncComponent(() =>
  import('@hypercodestudio/basler-components').then((m) => m.HeadlineBlock)
);

interface Props {
  componentName?: string;
  childProps?: unknown;
}

const props = defineProps<Props>();

const {
  public: { isPreviewEnabled }
} = useRuntimeConfig();
const logger = useLogger();

// `NuxtErrorBoundary` types its `error` slot prop as a plain `Error`, but at
// runtime Nuxt attaches its own `fatal` flag (see `NuxtError`/`H3Error`).
// This guard bridges that gap without casting in the template.
function isFatal(error: Error): boolean {
  return 'fatal' in error && Boolean(error.fatal);
}

function handleError(error: unknown) {
  logger.error('ErrorBoundary', error);
  // NuxtErrorBoundary catches all errors - including fatal errors.
  // A fatal error prevent navigation until it is cleared - therefore we
  // show a full screen error on fatal errors.
  if (error instanceof Error && isFatal(error)) {
    showError(error);
  }
}

const headline = computed(() => {
  return {
    content: `'Error rendering component ' + ${props.componentName ?? ''}`,
    level: 3 as const
  };
});
</script>
